import { Vec2 } from "../../../models/SyntheticMap";
import { blobToArrayBufferUsingFileReader, canvasToBlob } from "../../../utils/spatial";
import { FloorVisual } from "../../../visuals/FloorVisual";
import { SiteAnchorVisual } from "../../../visuals/SiteAnchorVisual";
import * as THREE from 'three';
import { RobotMap } from "./RobotMap";
import { loadTextureFromBuffer } from "../../../api/threed";
import { ModelBasedVisual } from "../../../visuals/ModelBasedVisual";
import { RobotVisual } from '../../../visuals/RobotVisual';
import { HumanVisual } from "../../../visuals/HumanVisual";
export class MapBuilder {
  public resolution = 0.2;
  constructor(public site: SiteAnchorVisual) { }

  public async build(flood = false, flip = false): Promise<RobotMap> {
    const width = this.site.grid.size;
    const height = this.site.grid.size;
    const { resolution } = this;
    const pixel_width = width / resolution;
    const pixel_height = height / resolution;
    var canvas = document.createElement("canvas");
    canvas.width = pixel_width;
    canvas.height = pixel_height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error('no ctx on map build');
    }
    if (flood) {
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    const convertToMapCoordinates = flip ?
      (x: number, y: number): Vec2 => {
        return {
          x: Math.round(x / resolution) + Math.round(pixel_height / 2),
          y: Math.round(-y / resolution) + Math.round(pixel_width / 2),
        }
      } :
      (x: number, y: number): Vec2 => {
        return {
          x: Math.round(x / resolution) + Math.round(pixel_height / 2),
          y: Math.round(y / resolution) + Math.round(pixel_width / 2),
        }
      };
    const pointToMap = (p: Vec2, parent: THREE.Object3D) => {
      const point = new THREE.Vector3(p.x, p.y, 0);
      point.applyQuaternion(parent.quaternion);
      point.add(parent.position);
      const cp = convertToMapCoordinates(point.x, point.y);
      return cp;
    };
    const renderLine = (p1: Vec2, p2: Vec2, thickness: number) => {
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.lineWidth = thickness;
      ctx.strokeStyle = "black";
      ctx.stroke();
    };
    const renderCircle = (p: Vec2, radius: number) => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
      ctx.fillStyle = "black";
      ctx.fill();
    };
    for (const visual of this.site.visuals.values()) {
      if (visual instanceof RobotVisual) {
        continue;
      }
      if (visual instanceof FloorVisual) {
        const lineThickness = 6;
        const floor = visual as FloorVisual;
        for (let i = 0; i < floor.outerWall.length - 1; i++) {
          const p1 = pointToMap(floor.outerWall[i], floor.obj);
          const p2 = pointToMap(floor.outerWall[i + 1], floor.obj);
          if (!floor.doorIndexes.includes(i)) {
            renderLine(p1, p2, lineThickness);
          }
          renderCircle(p1, lineThickness / 2);
          renderCircle(p2, lineThickness / 2);
        }

        if (!floor.doorIndexes.includes(floor.doorIndexes.length - 1)) {
          const p1 = pointToMap(floor.outerWall[floor.outerWall.length - 1], floor.obj);
          const p2 = pointToMap(floor.outerWall[0], floor.obj);
          renderLine(p1, p2, lineThickness);
        }
        for (let i = 0; i < floor.innerWalls.length; i++) {
          const innerWall = floor.innerWalls[i];
          for (let j = 0; j < innerWall.length - 1; j++) {
            const p1 = pointToMap(innerWall[j], floor.obj);
            const p2 = pointToMap(innerWall[j + 1], floor.obj);
            renderLine(p1, p2, lineThickness);
            renderCircle(p1, lineThickness / 2);
            renderCircle(p2, lineThickness / 2);
          }
        }
      } else if (visual instanceof HumanVisual) {
        // skip humans
      } else if (visual instanceof ModelBasedVisual) {
        const modelBased = visual as ModelBasedVisual;
        const boxes = modelBased.modelLoader.model.collisionMap.boxes;
        console.log('modelBased boxes', boxes.length);
        for (const box of boxes) {
          const corners = [
            new THREE.Vector3(box.position.x - box.size.x / 2, box.position.y + box.size.y / 2, 0),
            new THREE.Vector3(box.position.x - box.size.x / 2, box.position.y - box.size.y / 2, 0),
            new THREE.Vector3(box.position.x + box.size.x / 2, box.position.y - box.size.y / 2, 0),
            new THREE.Vector3(box.position.x + box.size.x / 2, box.position.y + box.size.y / 2, 0),
          ];
          // corners.forEach(c => {
          //   c = visual.obj.localToWorld(c);
          // });

          for (let i = 0; i < corners.length; i++) {
            const p1 = pointToMap(corners[i], modelBased.obj);
            const p2 = pointToMap(corners[(i + 1) % corners.length], modelBased.obj);
            renderLine(p1, p2, 5);
          }
        }
      }
    }

    const png = await canvasToBlob(canvas, 'image/png', 1);
    const data = await blobToArrayBufferUsingFileReader(png);
    const map: RobotMap = {
      robotId: 'preview',
      offsetX: -this.site.grid.size / 2,
      offsetY: -this.site.grid.size / 2,
      resolution,
      storageBlobId: '',
      data,
    }
    return map;
  }

  public async previewMap(map: RobotMap, flip = true, timeout = 0): Promise<THREE.Mesh> {
    const previewMesh = this.site.obj.getObjectByName('previewMap');
    if (previewMesh) {
      previewMesh.removeFromParent();
    }
    const geometry = new THREE.PlaneGeometry(1, 1);
    const material = new THREE.MeshBasicMaterial({
      color: 0x858585,
      side: THREE.DoubleSide,
      opacity: 0.65,
      transparent: true,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = 'previewMap';
    this.site.obj.add(mesh);
    const { width, height, texture } = await loadTextureFromBuffer(map.data!);
    mesh.geometry.dispose();
    const resolution = map.resolution;
    mesh.geometry = new THREE.PlaneGeometry(
      width * resolution,
      height * resolution
    );
    mesh.material = new THREE.MeshBasicMaterial({
      map: texture,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.65,
    });
    const scaledWidth = width * resolution;
    const scaledHeight = height * resolution;
    mesh.position.x = 0.5 * scaledWidth + map.offsetX;
    mesh.position.y = 0.5 * scaledHeight + map.offsetY;
    mesh.position.z = 0.15;
    console.log('preview map', map);
    if (flip) {
      mesh.rotation.x = Math.PI;
    }
    if (timeout) {
      setTimeout(() => {
        mesh.removeFromParent();
      }, timeout);
    }
    return mesh;
  }
}
